(() => {
  if (customElements.get('kin-product-fabric-pilot')) return;

  const selections = new Map();
  const palettes = new Map();
  let returnFocusPending = location.hash;
  const previewTheme = () => {
    const supplied = new URLSearchParams(location.search).get('preview_theme_id');
    const theme = window.Shopify?.theme;
    return /^\d+$/.test(supplied || '') ? supplied : theme?.role === 'unpublished' && /^\d+$/.test(String(theme.id)) ? String(theme.id) : '';
  };
  const retain = (map, key, value, maximum) => {
    map.delete(key);
    map.set(key, value);
    if (map.size > maximum) map.delete(map.keys().next().value);
  };
  const imageUrl = value => {
    if (typeof value !== 'string' || !value) return '';
    try {
      const url = new URL(value, location.origin);
      return ['https:', 'http:'].includes(url.protocol) ? url.href : '';
    } catch { return ''; }
  };

  const colourLabel = colour => {
    const name = typeof colour.name === 'string' ? colour.name.trim() : '';
    return [colour.code, name && name !== colour.code ? name : ''].filter(Boolean).join(' · ');
  };

  class KinProductFabricPilot extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      const productId = this.dataset.productId;
      if (!/^\d+$/.test(productId || '')) return;
      this.controller = new AbortController();
      this.request = 0;
      this.colours = [];
      this.choice = null;
      this.formId = this.dataset.formId;
      this.controls = {};
      for (const name of ['heading', 'status', 'search', 'grid', 'selection', 'selected-image', 'selected-text', 'remove', 'explore', 'details', 'retry', 'error', 'required', 'dialog', 'dialog-maker', 'dialog-title', 'dialog-image', 'dialog-colour', 'dialog-note', 'dialog-facts', 'dialog-close']) {
        this.controls[name] = this.querySelector(`[data-pilot-${name}]`);
      }
      this.fabricInput = this.querySelector('[data-pilot-property="fabric"]');
      const signal = this.controller.signal;
      document.addEventListener('submit', event => this.onSubmit(event), { capture: true, signal });
      this.controls.required.addEventListener('invalid', event => {
        event.preventDefault();
        this.showError(this.failureMessage());
        this.focusChoice();
      }, { signal });
      try {
        this.config = JSON.parse(this.querySelector('[data-pilot-config]').textContent);
        this.catalogue = JSON.parse(this.querySelector('#kin-fabric-catalogue').textContent);
        if (String(this.config.productId) !== productId || this.config.formId !== this.formId || !Array.isArray(this.config.fabricValues) || !this.config.fabricValues.length || !Array.isArray(this.catalogue.fabrics) || !Number.isInteger(this.config.fabricPosition) || this.config.fabricPosition < 1 || this.config.fabricPosition > 3 || !/^[a-z0-9][a-z0-9-]*$/.test(this.config.productHandle || '') || !/^[a-z][a-z0-9-]*$/.test(this.config.libraryScope || '') || !/^kin-[a-z0-9-]+-$/.test(this.config.paletteViewPrefix || '') || this.config.selectorId !== this.id) throw new Error();
        const productUrl = new URL(this.config.productUrl, location.origin);
        const libraryUrl = new URL(this.config.libraryUrl, location.origin);
        if (productUrl.origin !== location.origin || !productUrl.pathname.endsWith(`/products/${this.config.productHandle}`) || libraryUrl.origin !== location.origin || !/\/pages\/[^/]+$/.test(libraryUrl.pathname)) throw new Error();
        this.productPath = productUrl.pathname;
        this.libraryPath = libraryUrl.pathname;
        this.copy = this.config.copy;
        this.memoryKey = `${productId}:${this.formId}`;
        const incoming = new URLSearchParams(location.search);
        const returningVariant = this.currentVariant();
        const returningColour = incoming.get('colour') || '';
        const canRestore = returningVariant && String(returningVariant.id) === incoming.get('variant') && returningColour.length <= 80 && !/[\x00-\x1f<>]/.test(returningColour);
        this.memory = selections.get(this.memoryKey) || { fabric: canRestore ? returningVariant.options[this.config.fabricPosition - 1] : '', code: canRestore ? returningColour : '', pending: false };
        retain(selections, this.memoryKey, this.memory, 6);
        this.controls.heading.id ||= `${this.formId}-pilot-heading`;
        this.controls.grid.setAttribute('role', 'radiogroup');
        this.controls.grid.setAttribute('aria-labelledby', this.controls.heading.id);
        document.addEventListener('change', event => this.onNativeChange(event), { capture: true, signal });
        document.addEventListener('variant:change', event => {
          if (event.target === this.form()) this.queueVariantSync();
        }, { signal });
        this.form()?.addEventListener('product:rerender', () => this.queueVariantSync(), { signal });
        this.controls.search.addEventListener('input', () => this.renderGrid(), { signal });
        this.controls.grid.addEventListener('click', event => {
          const button = event.target.closest('[data-pilot-code]');
          if (button && this.controls.grid.contains(button)) this.choose(button.dataset.pilotCode);
        }, { signal });
        this.controls.grid.addEventListener('keydown', event => this.onGridKey(event), { signal });
        this.controls.required.addEventListener('change', () => this.choose(this.controls.required.value), { signal });
        this.controls.remove.addEventListener('click', () => {
          this.clearChoice();
          this.controls.status.textContent = this.copy.choose;
          this.focusChoice();
        }, { signal });
        this.controls.retry.addEventListener('click', () => {
          if (!this.memory.pending) this.syncVariant(true);
        }, { signal });
        this.controls.explore.addEventListener('click', event => {
          this.updateLinks();
          this.openDetails(event);
        }, { signal });
        this.controls['dialog-close'].addEventListener('click', () => this.closeDetails(), { signal });
        this.controls.dialog.addEventListener('cancel', event => {
          event.preventDefault();
          this.closeDetails();
        }, { signal });
        this.controls.dialog.addEventListener('keydown', event => {
          if (event.key !== 'Tab') return;
          const targets = [...this.controls.dialog.querySelectorAll('button:not([disabled]), a[href]')].filter(target => !target.hidden);
          const first = targets[0], last = targets[targets.length - 1];
          if (event.shiftKey && document.activeElement === first || !event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            (event.shiftKey ? last : first)?.focus();
          }
        }, { signal });
        this.controls.dialog.addEventListener('close', () => {
          document.documentElement.classList.remove('kin-fabric-pilot-modal-open');
          if (this.isConnected && this.choice) this.controls.explore.focus({ preventScroll: true });
        }, { signal });
        this.controls.dialog.addEventListener('click', event => {
          if (event.target !== this.controls.dialog) return;
          const bounds = this.controls.dialog.getBoundingClientRect();
          if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.closeDetails();
        }, { signal });
        this.controls.details.addEventListener('click', () => this.updateLinks(), { signal });
        this.isolateProductOptions();
        this.groupFabricOptions();
        this.syncVariant(true);
      } catch {
        this.fabricInput.value = '';
        this.controls.required.value = '';
        this.dataset.pilotState = 'error';
        this.dataset.pilotValid = 'false';
        this.showError(this.copy?.load_error || this.dataset.errorMessage);
      }
    }

    groupFabricOptions() {
      if (!Array.isArray(this.catalogue?.fabrics) || typeof this.config.priceGroupLabel !== 'string' || !this.config.priceGroupLabel) return;
      const brandGroups = new Map();
      for (const name of new Set(this.config.fabricValues)) {
        const records = this.catalogue.fabrics.filter(record => record.variant === name);
        if (records.length !== 1 || !Number.isInteger(records[0].group) || records[0].group < 1) return;
        const number = records[0].group;
        if (!brandGroups.has(number)) brandGroups.set(number, []);
        brandGroups.get(number).push(name);
      }
      const groups = [...brandGroups].map(([number, values]) => ({ number, values }));
      if (!groups.length) return;
      const picker = [...document.querySelectorAll('variant-picker')].find(candidate => candidate.getAttribute('form-id') === this.formId);
      const first = picker && [...picker.querySelectorAll('input[data-option-position]')].find(input => Number(input.dataset.optionPosition) === this.config.fabricPosition);
      const list = first?.closest('[data-option-selector]');
      const popover = list?.closest('x-popover');
      if (!list || !popover || list.dataset.fabricGroups === 'ready') return;
      const expected = new Set(this.config.fabricValues);
      const assigned = new Map(), numbers = new Set();
      for (const group of groups) {
        if (!group || !Number.isInteger(group.number) || group.number < 1 || numbers.has(group.number) || !Array.isArray(group.values) || !group.values.length) return;
        numbers.add(group.number);
        for (const name of group.values) {
          if (typeof name !== 'string' || !expected.has(name) || assigned.has(name)) return;
          assigned.set(name, group.number);
        }
      }
      if (assigned.size !== expected.size) return;
      const entries = [...list.children].map(label => ({ label, input: label.querySelector('input[type="radio"]'), name: label.textContent.trim() }));
      if (entries.length !== expected.size || new Set(entries.map(entry => entry.name)).size !== expected.size || entries.some(entry => !entry.label.matches('label.popover-listbox__option') || !entry.input || entry.input.getAttribute('form') !== this.formId || Number(entry.input.dataset.optionPosition) !== this.config.fabricPosition || !assigned.has(entry.name))) return;
      const fragment = document.createDocumentFragment();
      for (const group of [...groups].sort((a, b) => a.number - b.number)) {
        const section = document.createElement('div');
        const heading = document.createElement('p');
        section.className = 'kin-fabric-options__group';
        section.setAttribute('role', 'group');
        heading.id = `${popover.id}-price-group-${group.number}`;
        heading.className = 'kin-fabric-options__heading';
        heading.textContent = `${this.config.priceGroupLabel} ${group.number}`;
        section.setAttribute('aria-labelledby', heading.id);
        section.append(heading);
        const members = entries.filter(entry => assigned.get(entry.name) === group.number).sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base', numeric: true }));
        section.append(...members.map(entry => entry.label));
        fragment.append(section);
      }
      list.replaceChildren(fragment);
      list.dataset.fabricGroups = 'ready';
      popover.classList.add('kin-fabric-options');
      popover.setAttribute('initial-focus', 'input:checked');
    }

    isolateProductOptions() {
      const form = this.form();
      if (!form) return;
      const removeWidget = () => {
        for (const widget of form.querySelectorAll('gpo-container')) widget.remove();
      };
      removeWidget();
      this.appObserver = new MutationObserver(removeWidget);
      this.appObserver.observe(form, { childList: true, subtree: true });
    }

    disconnectedCallback() {
      this.appObserver?.disconnect();
      this.closeDetails();
      this.controller?.abort();
      this.controller = null;
      this.cancelLoad();
    }

    form() {
      const form = document.getElementById(this.formId);
      return form instanceof HTMLFormElement ? form : null;
    }

    currentVariant() {
      const value = this.form()?.elements.namedItem('id')?.value;
      if (!value) return null;
      const picker = [...document.querySelectorAll('variant-picker')].find(candidate => candidate.getAttribute('form-id') === this.formId && candidate.getAttribute('handle') === this.config.productHandle);
      if (!picker) return null;
      let rendered;
      try { rendered = JSON.parse(picker.querySelector('script[data-variant]')?.textContent || 'null'); } catch {}
      return [picker.selectedVariant, rendered].find(variant => variant && String(variant.id) === String(value) && Array.isArray(variant.options) && this.config.fabricValues.includes(variant.options[this.config.fabricPosition - 1])) || null;
    }

    queueVariantSync() {
      queueMicrotask(() => {
        if (this.isConnected && this.controller) this.syncVariant();
      });
    }

    onNativeChange(event) {
      const input = event.target;
      if (input.form !== this.form()) return;
      if (input.name === 'id') {
        this.queueVariantSync();
        return;
      }
      if (!input.matches('[data-option-position]')) return;
      this.closeDetails();
      this.memory.pending = true;
      if (Number(input.dataset.optionPosition) === this.config.fabricPosition) {
        this.cancelLoad();
        this.clearChoice();
        this.record = null;
        this.colours = [];
        this.controls.grid.replaceChildren();
        this.controls.search.value = '';
        this.memory.fabric = '';
        delete this.dataset.selectedFabric;
        this.controls.heading.textContent = this.copy.choose;
        this.updateLinks();
      }
      this.setState('pending');
      this.controls.status.textContent = this.copy.loading;
      this.showError('');
    }

    syncVariant(force = false) {
      const variant = this.currentVariant();
      this.memory.pending = false;
      const fabric = variant?.options?.[this.config.fabricPosition - 1];
      this.variant = variant;
      if (variant?.id) this.dataset.selectedVariant = String(variant.id);
      else delete this.dataset.selectedVariant;
      const matches = this.catalogue.fabrics.filter(record => typeof fabric === 'string' && record.variant === fabric);
      if (!variant || matches.length !== 1) {
        this.cancelLoad();
        this.record = null;
        this.colours = [];
        this.memory.fabric = '';
        this.clearChoice();
        delete this.dataset.selectedFabric;
        this.controls.grid.replaceChildren();
        this.setState('unavailable');
        this.controls.status.textContent = this.copy.unavailable;
        this.showError(this.copy.unavailable);
        this.updateLinks();
        return;
      }
      if (!force && this.record?.variant === fabric && this.colours.length) {
        this.setState('ready');
        this.syncProperties();
        this.updateLinks();
        this.controls.status.textContent = this.choice ? `${this.copy.selected} ${colourLabel(this.choice)}` : this.copy.choose;
        return;
      }
      const restoreCode = this.memory.fabric === fabric ? this.memory.code : '';
      if (this.memory.fabric !== fabric) this.clearChoice();
      this.memory.fabric = fabric;
      this.record = matches[0];
      this.dataset.selectedFabric = fabric;
      this.controls.heading.textContent = `${this.copy.colours} · ${this.record.name}`;
      this.controls.search.value = '';
      this.loadPalette(restoreCode);
    }

    cancelLoad() {
      this.request = (this.request || 0) + 1;
      this.fetchController?.abort();
      this.fetchController = null;
    }

    async loadPalette(restoreCode) {
      this.cancelLoad();
      const request = this.request;
      const record = this.record;
      this.choice = null;
      this.colours = [];
      this.syncProperties();
      this.controls.grid.replaceChildren();
      this.controls.required.replaceChildren(new Option(this.copy.choose, ''));
      this.controls.selection.hidden = true;
      this.controls.status.textContent = this.copy.loading;
      this.setState('loading');
      this.showError('');
      this.updateLinks();
      try {
        const url = new URL(record.dataUrl, location.origin);
        if (url.origin !== location.origin || url.pathname !== this.libraryPath || url.searchParams.get('view') !== `${this.config.paletteViewPrefix}${record.id}`) throw new Error();
        const preview = previewTheme();
        if (preview) url.searchParams.set('preview_theme_id', preview);
        let colours = palettes.get(url.href);
        if (!colours) {
          const controller = new AbortController();
          this.fetchController = controller;
          const timeout = setTimeout(() => controller.abort(), 15000);
          let data;
          try {
            const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
            if (!response.ok) throw new Error();
            data = await response.json();
          } finally { clearTimeout(timeout); }
          if (!Array.isArray(data.colours)) throw new Error();
          const codes = new Set();
          colours = data.colours.map(colour => {
            if (typeof colour.code !== 'string' || !colour.code || colour.code.length > 80 || codes.has(colour.code)) throw new Error();
            const thumb = imageUrl(colour.thumb), image = imageUrl(colour.image);
            if (!thumb || !image) throw new Error();
            codes.add(colour.code);
            return { code: colour.code, name: typeof colour.name === 'string' ? colour.name : '', aliases: Array.isArray(colour.aliases) ? colour.aliases.filter(alias => typeof alias === 'string') : [], thumb, image };
          });
          if (!colours.length) throw new Error();
          if (request !== this.request || !this.isConnected) return;
          retain(palettes, url.href, colours, 8);
        }
        if (request !== this.request || !this.isConnected || this.record !== record) return;
        this.colours = colours;
        for (const colour of colours) this.controls.required.add(new Option(colourLabel(colour), colour.code));
        this.setState(this.memory.pending ? 'pending' : 'ready');
        this.renderGrid();
        if (restoreCode && colours.some(colour => colour.code === restoreCode)) this.choose(restoreCode, true);
        else {
          this.memory.code = '';
          this.controls.status.textContent = this.memory.pending ? this.copy.loading : this.copy.choose;
          this.syncProperties();
        }
        this.restorePosition();
      } catch {
        if (request !== this.request || !this.isConnected) return;
        this.clearChoice();
        this.setState('error');
        this.controls.status.textContent = this.copy.load_error;
        this.showError(this.copy.load_error);
      }
    }

    setState(state) {
      this.dataset.pilotState = state;
      this.setAttribute('aria-busy', String(state === 'loading' || state === 'pending'));
      this.controls.search.disabled = state !== 'ready';
      this.controls.retry.hidden = state !== 'error';
      this.controls.grid.querySelectorAll('button').forEach(button => { button.disabled = state !== 'ready'; });
      this.updateValidity();
    }

    renderGrid() {
      const query = this.controls.search.value.toLocaleLowerCase().trim();
      const visible = this.colours.filter(colour => [colour.code, colour.name, ...colour.aliases].join(' ').toLocaleLowerCase().includes(query));
      const fragment = document.createDocumentFragment();
      for (const colour of visible) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'kin-fabric-pilot__swatch';
        button.dataset.pilotCode = colour.code;
        button.setAttribute('role', 'radio');
        button.setAttribute('aria-label', [this.record.name, colourLabel(colour)].filter(Boolean).join(' · '));
        button.setAttribute('aria-checked', String(this.choice?.code === colour.code));
        button.disabled = this.dataset.pilotState !== 'ready';
        const image = document.createElement('img');
        image.src = colour.thumb;
        image.alt = '';
        image.width = 64;
        image.height = 64;
        image.loading = 'lazy';
        const label = document.createElement('span');
        label.textContent = colour.code;
        button.append(image, label);
        fragment.append(button);
      }
      this.controls.grid.replaceChildren(fragment);
      this.syncButtons();
      this.controls.status.textContent = visible.length ? (this.choice ? `${this.copy.selected} ${colourLabel(this.choice)}` : this.copy.choose) : this.copy.no_results;
    }

    syncButtons() {
      const buttons = [...this.controls.grid.querySelectorAll('[data-pilot-code]')];
      const active = buttons.find(button => button.dataset.pilotCode === this.choice?.code) || buttons[0];
      for (const button of buttons) {
        button.setAttribute('aria-checked', String(button.dataset.pilotCode === this.choice?.code));
        button.tabIndex = button === active ? 0 : -1;
      }
    }

    choose(code, restore = false) {
      if ((!restore && this.dataset.pilotState !== 'ready') || !this.record) return;
      const colour = this.colours.find(candidate => candidate.code === code);
      if (!colour) { this.clearChoice(); return; }
      this.choice = colour;
      this.memory.code = code;
      this.memory.fabric = this.record.variant;
      this.controls.required.value = code;
      this.controls.selection.hidden = false;
      this.controls['selected-image'].src = colour.image;
      this.controls['selected-image'].alt = [this.record.name, colourLabel(colour)].filter(Boolean).join(' · ');
      this.controls['selected-text'].textContent = [this.record.name, colourLabel(colour)].filter(Boolean).join(' · ');
      this.controls.status.textContent = this.memory.pending ? this.copy.loading : `${this.copy.selected} ${colourLabel(colour)}`;
      this.showError('');
      this.syncButtons();
      this.syncProperties();
      this.rememberColour();
      this.updateLinks();
    }

    clearChoice() {
      this.closeDetails();
      this.choice = null;
      if (this.memory) this.memory.code = '';
      this.controls.required.value = '';
      this.controls.selection.hidden = true;
      this.controls['selected-image'].removeAttribute('src');
      this.controls['selected-image'].alt = '';
      this.controls['selected-text'].textContent = '';
      this.syncButtons();
      this.syncProperties();
      this.showError('');
      this.rememberColour();
      this.updateLinks();
    }

    syncProperties() {
      const chosen = this.choice && this.record && this.colours.some(colour => colour.code === this.choice.code);
      this.fabricInput.value = chosen ? this.record.variant : '';
      this.controls.required.value = chosen ? this.choice.code : '';
      if (chosen) this.dataset.selectedColour = this.choice.code;
      else delete this.dataset.selectedColour;
      this.updateValidity();
    }

    valid() {
      if (!this.memory) return false;
      const current = this.currentVariant();
      return Boolean(this.dataset.pilotState === 'ready' && !this.memory.pending && current?.available === true && String(current.id) === String(this.variant?.id) && this.record?.variant === current.options?.[this.config.fabricPosition - 1] && this.choice && this.colours.some(colour => colour.code === this.choice.code) && this.controls.required.value === this.choice.code && this.fabricInput.value === this.record.variant);
    }

    updateValidity() {
      this.dataset.pilotValid = String(this.memory ? this.valid() : false);
    }

    failureMessage() {
      if (!this.memory) return this.copy?.load_error || this.dataset.errorMessage;
      if (this.memory.pending || ['loading', 'pending'].includes(this.dataset.pilotState)) return this.copy.loading;
      if (this.dataset.pilotState === 'error') return this.copy.load_error;
      if (this.dataset.pilotState === 'unavailable' || this.currentVariant()?.available !== true) return this.copy.unavailable;
      return this.copy.required;
    }

    onSubmit(event) {
      if (event.target !== this.form() || this.valid()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.showError(this.failureMessage());
      this.focusChoice();
      if (this.memory && !this.memory.pending && this.currentVariant()?.id !== this.variant?.id) this.queueVariantSync();
    }

    showError(message) {
      this.controls.error.textContent = message;
      this.controls.error.hidden = !message;
      this.controls.required.setAttribute('aria-invalid', String(Boolean(message)));
    }

    focusChoice() {
      const target = this.controls.grid.querySelector('[tabindex="0"]:not(:disabled)') || (!this.controls.retry.hidden && this.controls.retry) || this;
      target?.focus();
    }

    openDetails(event) {
      const dialog = this.controls.dialog;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0 || !dialog?.showModal || !this.choice || !this.record || this.dataset.pilotState !== 'ready') return;
      event.preventDefault();
      const identity = [this.record.name, colourLabel(this.choice)].filter(Boolean).join(' · ');
      this.controls['dialog-maker'].textContent = this.record.maker || '';
      this.controls['dialog-title'].textContent = this.record.name;
      this.controls['dialog-image'].src = this.choice.image;
      this.controls['dialog-image'].alt = identity;
      this.controls['dialog-colour'].textContent = identity;
      const note = typeof this.record.note === 'string' ? this.record.note.trim() : '';
      this.controls['dialog-note'].textContent = note;
      this.controls['dialog-note'].hidden = !note;
      const facts = document.createDocumentFragment();
      for (const [key, value] of [['composition', this.record.composition], ['abrasion', this.record.abrasion], ['pilling', this.record.pilling], ['lightfastness', this.record.light]]) {
        if (!['string', 'number'].includes(typeof value) || !String(value).trim() || !this.config.factLabels?.[key]) continue;
        const row = document.createElement('div'), label = document.createElement('dt'), detail = document.createElement('dd');
        label.textContent = this.config.factLabels[key];
        detail.textContent = String(value);
        row.append(label, detail);
        facts.append(row);
      }
      this.controls['dialog-facts'].replaceChildren(facts);
      this.controls['dialog-facts'].hidden = !this.controls['dialog-facts'].children.length;
      if (!dialog.open) dialog.showModal();
      document.documentElement.classList.add('kin-fabric-pilot-modal-open');
    }

    closeDetails() {
      if (this.controls?.dialog?.open) this.controls.dialog.close();
      document.documentElement.classList.remove('kin-fabric-pilot-modal-open');
    }

    onGridKey(event) {
      const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
      if (!keys.includes(event.key) || !event.target.matches('[data-pilot-code]')) return;
      const buttons = [...this.controls.grid.querySelectorAll('[data-pilot-code]:not(:disabled)')];
      if (!buttons.length) return;
      event.preventDefault();
      const index = buttons.indexOf(event.target);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (index + (['ArrowLeft', 'ArrowUp'].includes(event.key) ? -1 : 1) + buttons.length) % buttons.length;
      this.choose(buttons[next].dataset.pilotCode);
      buttons[next].focus();
    }

    rememberColour() {
      const url = new URL(location.href);
      if (this.choice && this.record?.variant === this.currentVariant()?.options?.[this.config.fabricPosition - 1]) url.searchParams.set('colour', this.choice.code);
      else url.searchParams.delete('colour');
      if (url.href !== location.href) history.replaceState(history.state, '', url);
    }

    restorePosition() {
      if (returnFocusPending !== `#${this.config.selectorId}`) return;
      requestAnimationFrame(() => {
        if (!this.isConnected || returnFocusPending !== `#${this.config.selectorId}`) return;
        returnFocusPending = '';
        const target = this.controls.grid.querySelector('[aria-checked="true"]') || this;
        target.focus({ preventScroll: true });
        target.scrollIntoView({ block: 'center' });
      });
    }

    returnUrl() {
      const current = new URL(location.href);
      const previewKey = current.searchParams.get('preview_key');
      const preview = current.pathname === '/products_preview' && /^[a-f0-9]{32}$/i.test(previewKey || '');
      const url = new URL(preview ? '/products_preview' : this.productPath, location.origin);
      if (preview) url.searchParams.set('preview_key', previewKey);
      const variant = this.currentVariant();
      if (variant) url.searchParams.set('variant', String(variant.id));
      if (this.choice) url.searchParams.set('colour', this.choice.code);
      if (previewTheme()) url.searchParams.set('preview_theme_id', previewTheme());
      url.hash = this.config.selectorId;
      return url.pathname + url.search + url.hash;
    }

    updateLinks() {
      if (!this.catalogue) return;
      const model = this.config.productHandle;
      for (const [link, destination, hash] of [[this.controls.explore, this.record?.url || this.catalogue.guideUrl, this.record ? null : 'fabric-library'], [this.controls.details, this.record?.url || this.catalogue.guideUrl, this.record ? null : 'fabric-library']]) {
        if (!link || !destination) continue;
        try {
          const url = new URL(destination, location.origin);
          if (url.origin !== location.origin || !url.pathname.includes('/pages/')) continue;
          url.searchParams.set('model', model);
          const variant = this.currentVariant();
          if (variant) url.searchParams.set('variant', String(variant.id));
          else url.searchParams.delete('variant');
          if (previewTheme()) url.searchParams.set('preview_theme_id', previewTheme());
          url.searchParams.set('fabric_from', this.returnUrl());
          url.searchParams.set('fabric_from_label', this.config.returnLabel);
          // A top-level colour opens the library picker; the original choice stays in fabric_from.
          if (this.choice && link !== this.controls.details) url.searchParams.set('colour', this.choice.code);
          else url.searchParams.delete('colour');
          if (hash) url.hash = hash;
          link.href = url.href;
        } catch {}
      }
    }
  }

  customElements.define('kin-product-fabric-pilot', KinProductFabricPilot);
})();
